import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { ListenerErrors, type ListenerErrorSink, type Thrown } from "@tento-core/listener-errors";
import { TypedApi } from "@tento-core/typed-api";
import { Network } from "../network.ts";
import { CellEntry, RowKey, SdkRequestError } from "@tento-lona/sheets";
import type { CellValue, TimepointCellEntry } from "@tento-lona/sheets";
import type { CellHandle } from "./cell-handle.ts";
import { cellReadFailure } from "../client/handle-errors.ts";
import type { RowCellCache } from "../sync/row-cell-cache.ts";
import { RowNetworkKey } from "@tento-lona/sheets";

/**
 * A row's progressive range read (`row.cells(range)`): it fetches through the client's API and
 * follows the row handle's sync state. Mirrors `CellHandle` but returns
 * arrays of entries.
 *
 * Two-phase callback model:
 * - `onLoading()` — fires when no data is cached and a fetch is pending
 * - `onData(entries)` — fires whenever data is available (may re-fire on sync updates)
 *
 * `onDataFull(entries)` fires only when sync is fully complete — and, for a
 * handle over a `RowCellCache`, only once the fetch of the REQUESTED window
 * answered: cached cells may reach `onData` earlier, but another window's
 * coverage never settles this one (nor paints it as `[]`). The awaited
 * handle resolves with the same entries, at the same moment.
 *
 * `onError(err)` fires when the read failed — registered after that, it
 * fires at once — and the awaited handle rejects with the same error: an
 * `SdkRequestError` for a refused, unanswered, malformed or faulted read (a failure
 * is never read as `[]`; a row the server does not know, a 404, reads as
 * `[]`), or the owner's disposal error when what handed the handle out
 * (`CellHandle.Owner`) was disposed before the read started or while it was
 * active (the subscription is released first; no `onData` after it):
 * `SheetHandle.DisposedError` for a sheet's row, and
 * `LonaSdk.Client.DisposedError` for a standalone row
 * (`client.rows.open(...)`) whose client was disposed — its shared
 * `RowCellCache` is left as it is. A user callback that disposes the owner or
 * the handle stops
 * the read where it stands: after `onLoading`, no subscription or request
 * starts; after `onData`, no `onDataFull` follows.
 *
 * Implements PromiseLike so it can be awaited for simple use cases.
 */
export class RowRangeCellHandle<T = CellEntry>
  implements PromiseLike<T[]>, Disposable
{
  #loadingCallback: Option<() => void> = null;
  #dataCallback: Option<(entries: T[]) => void> = null;
  #dataFullCallback: Option<(entries: T[]) => void> = null;
  #errorCallback: Option<(err: CellHandle.Failure) => void> = null;
  /** The read's failure, replayed to a late `onError`. */
  #failure: Option<{ readonly err: CellHandle.Failure }> = null;
  #disposed = false;
  #settle: Option<{
    readonly resolve: (value: T[]) => void;
    readonly reject: (err: CellHandle.Failure) => void;
  }> = null;
  readonly #promise: Promise<T[]>;
  #unsubscribe: Option<() => void> = null;
  /** Stops watching the owner's disposal; set while subscribed. */
  #unwatchOwner: Option<() => void> = null;
  readonly #fetchEntries: () => Promise<T[]>;
  readonly #rowHandle: Option<RowCellCache>;
  readonly #readCached: Option<() => T[]>;
  readonly #utcRange: Option<DateTime.Range<Utc>>;
  readonly #owner: Option<CellHandle.Owner>;
  /** Where a callback's error goes when no await receives it: the row's sink, else the one given. */
  readonly #listenerErrors: ListenerErrorSink;
  /** Someone awaits this read (`then` was called). */
  #awaited = false;

  /**
   * `fetchEntries` is the checked read of a handle with no `RowCellCache`: it
   * rejects on a failed request. `utcRange` is the range a cache-backed
   * handle asks its `RowCellCache` to fetch on start (`null`: no fetch
   * trigger). `owner` is the sheet (or a standalone row's client) that
   * hands the handle out (checked when the deferred read starts, watched
   * while it is active); `null` when nothing owns it. `listenerErrors` is
   * where a callback's error goes when no await receives it, for a handle
   * with no row cache (a cache-backed handle uses its row's sink).
   */
  constructor(
    fetchEntries: () => Promise<T[]>,
    rowHandle: Option<RowCellCache>,
    readCached: Option<() => T[]>,
    utcRange: Option<DateTime.Range<Utc>>,
    owner: Option<CellHandle.Owner>,
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
  ) {
    this.#listenerErrors = rowHandle?.listenerErrors ?? listenerErrors;
    this.#fetchEntries = fetchEntries;
    this.#rowHandle = rowHandle;
    this.#readCached = readCached;
    this.#utcRange = utcRange;
    this.#owner = owner;
    this.#promise = new Promise<T[]>((resolve, reject) => {
      this.#settle = { resolve, reject };
    });
    // An un-awaited handle that fails must not surface as an unhandled
    // rejection: `onError` is how a subscriber hears it, `then` how an
    // awaiting caller does (a fresh chain that still rejects).
    this.#promise.catch(() => {});
    // Defer start so callers can chain onLoading/onData before fetch begins.
    queueMicrotask(() => this.#startLoading());
  }

  /** Fires when no data is cached and a fetch is in progress. */
  onLoading(cb: () => void): this {
    this.#loadingCallback = cb;
    return this;
  }

  /** Fires whenever data is available (may re-fire on sync updates). */
  onData(cb: (entries: T[]) => void): this {
    this.#dataCallback = cb;
    return this;
  }

  /** Fires only when sync is fully complete. */
  onDataFull(cb: (entries: T[]) => void): this {
    this.#dataFullCallback = cb;
    return this;
  }

  /** Fires when the read failed — at once when it already failed. `err` is
   *  the read's `SdkRequestError`, or the owner's disposal error
   *  (`SheetHandle.DisposedError`, `LonaSdk.Client.DisposedError`; see the
   *  class doc). */
  onError(cb: (err: CellHandle.Failure) => void): this {
    this.#errorCallback = cb;
    const failure = this.#failure;
    if (failure != null && !this.#disposed) cb(failure.err);
    return this;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  /** Stop the callbacks and release the row subscription. Idempotent. An
   *  awaited handle disposed before it settled never settles. */
  dispose(): void {
    this.#disposed = true;
    this.#loadingCallback = null;
    this.#dataCallback = null;
    this.#dataFullCallback = null;
    this.#errorCallback = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#unwatchOwner?.();
    this.#unwatchOwner = null;
  }

  [Symbol.dispose](): void {
    this.dispose();
  }

  // biome-ignore lint/suspicious/noThenProperty: PromiseLike implementation
  then<TResult1 = T[], TResult2 = never>(
    onfulfilled: Option<(value: T[]) => TResult1 | PromiseLike<TResult1>> =
      null,
    // `unknown` is the PromiseLike contract: a rejection reason is untyped.
    onrejected: Option<(reason: unknown) => TResult2 | PromiseLike<TResult2>> =
      null,
  ): Promise<TResult1 | TResult2> {
    this.#awaited = true;
    return this.#promise.then(onfulfilled, onrejected);
  }

  /** The read stopped: this handle disposed, or it failed (a failure is
   *  also how the owning sheet's disposal arrives, through its watcher). */
  #stopped(): boolean {
    return this.#disposed || this.#failure != null;
  }

  /** Release the row subscription (this subscriber only: the `RowCellCache`
   *  is shared). */
  #release(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #startLoading(): void {
    if (this.#disposed) return;
    // The owner (sheet, or a standalone row's client) may have been
    // disposed before this microtask: no request then, the refusal goes to
    // `onError` and the rejection.
    const owner = this.#owner;
    try {
      owner?.assertLive();
    } catch (err) {
      this.#fail(err);
      return;
    }
    // Watch the owner BEFORE the first user callback: `onLoading` or
    // `onData` may dispose it. Disposed while active:
    // release the subscription, then report.
    this.#unwatchOwner = owner?.watch((err) => {
      this.#unwatchOwner = null;
      this.#release();
      this.#fail(err);
    }) ?? null;

    const rh = this.#rowHandle;
    if (rh) {
      this.#guarded(() => this.#startWithRowHandle(rh));
    } else {
      void this.#startWithFetch().catch((error) => this.#abandon(error));
    }
  }

  /**
   * Run `step`, which calls the caller's own callbacks: one that throws
   * abandons the read (`#abandon`) instead of escaping into whatever
   * delivered the step — the deferred start, the row's fan-out, or the
   * window's fetch confirmation (which nothing awaits).
   */
  #guarded(step: () => void): void {
    try {
      step();
    } catch (error) {
      this.#abandon(error);
    }
  }

  /**
   * A caller's callback threw: this read stops (the row subscription and the
   * owner watch are released) and the error reaches the caller exactly once
   * — it is the caller's own code, not a request failure, so not `onError`.
   * An outstanding await rejects with it. Whether anyone awaits is decided
   * the way an unhandled rejection is, one task later (an `await` may attach
   * after the callback ran): nobody did, or the read had already settled,
   * and the error is reported out of band to the row's `ListenerErrorSink`
   * (the client's `listenerErrorHandler`) instead. Never swallowed, never an
   * unhandled rejection. `CellHandle` (not awaitable) stops the same way and
   * always reports.
   */
  #abandon(error: Thrown): void {
    const settle = this.#settle;
    this.#settle = null;
    this.dispose();
    settle?.reject(error);
    const report = () => ListenerErrors.report(this.#listenerErrors, [error], "RowRangeCellHandle");
    if (settle == null) report();
    else setTimeout(() => { if (!this.#awaited) report(); }, 0);
  }

  #startWithRowHandle(rh: RowCellCache): void {
    const range = this.#utcRange;
    // The requested window is answered only by this handle's own fetch of
    // it: `rh.hasCoverage` says SOME window is known, not this one. With no
    // fetch trigger (`utcRange` null) the row's settled
    // coverage is all there is to go on.
    let windowFetched = false;
    const settled = (): boolean =>
      range == null ? rh.hasCoverage && !rh.isFetchPending : windowFetched;
    // Whether `onData` already has the cache as it stands: a row change
    // resets it; the window's confirmation alone does not repaint.
    let delivered = false;
    const evaluate = () => {
      if (this.#stopped()) return;
      const cached = this.#readCached?.() ?? [];
      const complete = settled();

      // Loading = nothing cached AND the server has not answered for the
      // requested window: no coverage at all, coverage of another window,
      // or the only "coverage" is the optimistic fetchedRange claimed at
      // fetch START while the fetch is still in flight
      // (`rh.isFetchPending`). Without the pending check, the first
      // subscriber's fetch made every later evaluate fire `onData([])` — a
      // blank/stale paint that stomped the loading shimmer
      // (weather-relocation regression, session
      // 2026-07-03_dataref-flash-scoped-clone_6f621d).
      if (
        cached.length === 0 && !complete &&
        (!rh.hasCoverage || rh.isFetchPending || !coversWindow(rh, range))
      ) {
        this.#loadingCallback?.();
        return;
      }

      // Progressive: cached data may be painted before the window is
      // confirmed; the handle completes only once it is.
      if (!delivered) {
        delivered = true;
        this.#dataCallback?.(cached);
      }
      // `onData` may dispose this handle or its sheet: check again before
      // the next user callback.
      if (this.#stopped() || !complete || rh.syncState) return;
      this.#dataFullCallback?.(cached);
      if (this.#stopped()) return;
      // Still subscribed (later row changes re-deliver), so the owner stays
      // watched: its disposal releases the subscription.
      this.#settle?.resolve(cached);
      this.#settle = null;
    };

    evaluate();
    // A callback of the first evaluate stopped the read: subscribe to
    // nothing and fetch nothing.
    if (this.#stopped()) return;
    this.#unsubscribe = rh.onChange(() => {
      delivered = false;
      this.#guarded(evaluate);
    });

    // Trigger the fetch; a failed one fails the handle instead of leaving
    // it on its placeholder (and rejects the await: it never settled on
    // another window's coverage). Nothing awaits this confirmation, so a
    // callback it runs that throws (`onData`, `onDataFull`, `onError`)
    // abandons the read rather than becoming an unhandled rejection that
    // leaves the await pending and the owner watched.
    if (range == null) return;
    rh.fetch(range).then(
      () => {
        if (this.#stopped()) return;
        windowFetched = true;
        this.#guarded(evaluate);
      },
      (err: Thrown) => {
        if (this.#stopped()) return;
        this.#release();
        this.#guarded(() => this.#fail(err));
      },
    );
  }

  async #startWithFetch(): Promise<void> {
    this.#loadingCallback?.();
    // `onLoading` may dispose this handle or its sheet: no request then.
    if (this.#stopped()) return;
    let entries: T[];
    try {
      entries = await this.#fetchEntries();
    } catch (err) {
      this.#fail(err);
      return;
    }
    if (this.#stopped()) return;
    this.#dataCallback?.(entries);
    if (this.#stopped()) return;
    this.#dataFullCallback?.(entries);
    if (this.#stopped()) return;
    this.#unwatchOwner?.();
    this.#unwatchOwner = null;
    this.#settle?.resolve(entries);
    this.#settle = null;
  }

  /**
   * Record `err` (replayed to a late `onError`), report it and reject. The
   * outstanding await is rejected whatever the host's `onError` does: a
   * throwing callback still propagates (a disposal collects it into its
   * `AggregateError`), but only after the rejection is settled — the
   * failure is recorded and the subscription released, so nothing later
   * could settle it.
   */
  #fail(err: unknown): void {
    if (this.#stopped()) return;
    const failure = cellReadFailure(err);
    this.#failure = { err: failure };
    this.#unwatchOwner?.();
    this.#unwatchOwner = null;
    this.#release();
    const settle = this.#settle;
    this.#settle = null;
    try {
      this.#errorCallback?.(failure);
    } finally {
      settle?.reject(failure);
    }
  }
}

/** Whether `rh`'s settled coverage (fetched or vacuous) spans `range` —
 *  both ends, not merely "some window is known". */
function coversWindow(
  rh: RowCellCache,
  range: Option<DateTime.Range<Utc>>,
): boolean {
  if (range == null) return true;
  if (!rh.isCoveredAt(range.start)) return false;
  if (range.end.mse <= range.start.mse) return true;
  return rh.isCoveredAt(range.end.sub({ ms: 1 }));
}

// ── Private helpers ─────────────────────────────────────────────────

/**
 * `GET /api/rows/{rowKey}/cells` over `[start, end)`, checked: a 404 (no
 * such row) reads as `[]`; a refused, unanswered or malformed read throws
 * `SdkRequestError` (boundary `cells`, step `get-cells`) — never `[]`.
 */
async function fetchCellsChecked(
  api: TypedApi<Network.Routes>,
  rowKey: RowNetworkKey,
  start: NaiveDate,
  end: NaiveDate,
): Promise<Network.SheetCell[]> {
  const path = "/api/rows/{rowKey}/cells";
  const cells = await SdkRequestError.checkRequest(
    "cells",
    "get-cells",
    "GET",
    path,
    () =>
      SdkRequestError.notFoundAsNull(() =>
        api.getResult(path, {
          path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
          query: { start, end, mock_sync_delay: null, booking_id: null },
        })
      ),
  );
  return cells ?? [];
}

function networkCellAsDateCell(cell: Network.SheetCell): Option<CellEntry> {
  const partial = NaiveDate.Partial.parse(cell.date);
  if (partial.isOk) {
    return {
      date: nonnull(partial.exp(), "networkCellAsDateCell: ok partial"),
      id: null,
      value: cell.data ?? null,
      style: null,
    };
  }

  const dt = DateTime.fromRfc3339(cell.date);
  if (!dt.isOk) return null;
  return {
    date: NaiveDate.Partial.fromDate(
      nonnull(dt.exp(), "networkCellAsDateCell: ok datetime").date,
    ),
    id: null,
    value: cell.data ?? null,
    style: null,
  };
}

function customCellsAsDateCells(
  custom: readonly TimepointCellEntry[],
): CellEntry[] {
  return custom.map((cell) => ({
    date: NaiveDate.Partial.fromDate(cell.dtr.start.date),
    id: null,
    value: cell.value,
    style: null,
  }));
}

/** Values that go with `RowRangeCellHandle`: `make`, `makeTimepoint`. */
export namespace RowRangeCellHandle {
  /**
   * Make a RowRangeCellHandle for a NaiveDate range (date-granularity
   * cells). `owner` is the sheet (or standalone row's client) handing it
   * out (`null`: none).
   */
  export function make(
    rowKey: RowNetworkKey,
    range: NaiveDate.Range,
    api: TypedApi<Network.Routes>,
    rh: Option<RowCellCache>,
    owner: Option<CellHandle.Owner>,
  ): RowRangeCellHandle<CellEntry> {
    const fetchFn = async (): Promise<CellEntry[]> => {
      const cells = await fetchCellsChecked(
        api,
        rowKey,
        range.start,
        range.end,
      );
      const entries: CellEntry[] = [];
      for (const cell of cells) {
        const entry = networkCellAsDateCell(cell);
        if (entry) entries.push(entry);
      }
      return entries;
    };

    const getCached = rh
      ? () => {
        const dateCells = rh.getAllInRange(range);
        if (dateCells.length > 0) return dateCells;
        const utcRange = new DateTime.Range(
          DateTime.fromDse(range.start.dse, Utc),
          DateTime.fromDse(range.end.dse, Utc),
        );
        return customCellsAsDateCells(rh.getAllTimepointsInRange(utcRange));
      }
      : null;

    const utcRange = new DateTime.Range(
      DateTime.fromDse(range.start.dse, Utc),
      DateTime.fromDse(range.end.dse, Utc),
    );
    return new RowRangeCellHandle<CellEntry>(fetchFn, rh, getCached, utcRange, owner);
  }

  /**
   * Make a RowRangeCellHandle for a DateTime.Range<Utc> (sub-day
   * precision). `owner` is the sheet (or standalone row's client) handing
   * it out (`null`: none).
   */
  export function makeTimepoint(
    rowKey: RowNetworkKey,
    range: DateTime.Range<Utc>,
    api: TypedApi<Network.Routes>,
    rh: Option<RowCellCache>,
    owner: Option<CellHandle.Owner>,
  ): RowRangeCellHandle<TimepointCellEntry> {
    const fetchFn = async (): Promise<TimepointCellEntry[]> => {
      const cells = await fetchCellsChecked(
        api,
        rowKey,
        range.start.date,
        range.end.date,
      );
      // Server contract (`tento_lona_connector::Cell::point` +
      // wire `try_from = "RawCell"`): point-time cells always carry
      // an id. `CellEntry.Timepoint.from` enforces it — silently
      // swallowing wire-contract violations is exactly what we are
      // trying to stop here.
      return cells.map((c) => CellEntry.Timepoint.from(c));
    };

    const getCached = rh
      ? () => rh.getAllTimepointsInRange(range)
      : null;

    return new RowRangeCellHandle<TimepointCellEntry>(fetchFn, rh, getCached, range, owner);
  }
}
